import { Building2, Briefcase, ArrowLeftRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface DualAccess {
  has_monument_access: boolean;
  has_partner_access: boolean;
  monument_company_id: string | null;
  fulfillment_partner_id: string | null;
}

const PortalSwitcher = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isMonumentPortal = location.pathname.startsWith('/portal');

  const { data: dualAccess } = useQuery({
    queryKey: ['dual-portal-access', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .rpc('has_dual_portal_access', { _user_id: user.id });

      if (error) throw error;
      return data?.[0] as DualAccess | null;
    },
    enabled: !!user?.id,
  });

  // Only show if user has access to both portals
  if (!dualAccess?.has_monument_access || !dualAccess?.has_partner_access) {
    return null;
  }

  const handleSwitch = () => {
    if (isMonumentPortal) {
      navigate('/partner/dashboard');
    } else {
      navigate('/portal/dashboard');
    }
  };

  return (
    <Button variant="outline" size="sm" className="gap-2 h-9" onClick={handleSwitch}>
      {isMonumentPortal ? (
        <>
          <Briefcase className="h-4 w-4" />
          <span className="hidden sm:inline">Switch to Fulfillment Portal</span>
          <ArrowLeftRight className="h-3.5 w-3.5 sm:hidden" />
        </>
      ) : (
        <>
          <Building2 className="h-4 w-4" />
          <span className="hidden sm:inline">Switch to Monument Portal</span>
          <ArrowLeftRight className="h-3.5 w-3.5 sm:hidden" />
        </>
      )}
    </Button>
  );
};

export default PortalSwitcher;
